import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { CurrencyProvider } from './context/CurrencyContext';
import PublicLayout from './components/layout/PublicLayout';
import ScrollToTop from './components/layout/ScrollToTop';
import ErrorBoundary from './components/common/ErrorBoundary';
import PageLoader from './components/common/PageLoader';
import { PageVisibilityGate, PageVisibilityProvider } from './context/PageVisibilityContext';

// Core pages (loaded eagerly - most visited)
import Home from './pages/Home';
import Shop from './pages/ServerShop';
import BookDetail from './pages/BookDetail';

// Lazy loaded pages
const Cart = lazy(() => import('./pages/Cart'));
const Wishlist = lazy(() => import('./pages/profile/Wishlist'));
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/profile/OrderSuccessPage'));
const Notifications = lazy(() => import('./pages/profile/Notifications'));
const Contact = lazy(() => import('./pages/static/Contact'));
const About = lazy(() => import('./pages/static/About'));
const Career = lazy(() => import('./pages/static/Career'));
const Blog = lazy(() => import('./pages/blog/Blog'));
const BlogDetail = lazy(() => import('./pages/blog/BlogDetail'));
const FeaturedVideo = lazy(() => import('./pages/blog/FeaturedVideo'));
const Privacy = lazy(() => import('./pages/legal/Privacy'));
const Terms = lazy(() => import('./pages/legal/Terms'));
const SecureShopping = lazy(() => import('./pages/legal/SecureShopping'));
const Copyright = lazy(() => import('./pages/legal/Copyright'));
const Payment = lazy(() => import('./pages/legal/Payment'));
const Shipping = lazy(() => import('./pages/legal/Shipping'));
const Return = lazy(() => import('./pages/legal/Return'));
const FAQ = lazy(() => import('./pages/static/FAQ'));
const Affiliate = lazy(() => import('./pages/static/Affiliate'));
const Sitemap = lazy(() => import('./pages/static/Sitemap'));
const NotFound = lazy(() => import('./pages/NotFound'));

const visiblePage = (pageKey, element) => (
  <PageVisibilityGate pageKey={pageKey}>{element}</PageVisibilityGate>
);

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <ToastProvider>
              <ConfirmProvider>
                <CurrencyProvider>
                  <Router>
                    <PageVisibilityProvider>
                      <ScrollToTop />
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route element={<PublicLayout />}>
                            <Route path="/" element={<Home />} />
                            <Route path="/shop" element={<Shop />} />
                            <Route path="/book/:id" element={visiblePage('book', <BookDetail />)} />
                            <Route path="/cart" element={<Cart />} />
                            <Route path="/wishlist" element={<Wishlist />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password/:token" element={<ResetPassword />} />
                            <Route path="/verify-email/:token" element={<VerifyEmail />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/contact" element={visiblePage('contact', <Contact />)} />
                            <Route path="/about" element={visiblePage('about', <About />)} />
                            <Route path="/career" element={visiblePage('career', <Career />)} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:id" element={<BlogDetail />} />
                            <Route path="/featured-video" element={visiblePage('featured', <FeaturedVideo />)} />
                            <Route path="/privacy" element={visiblePage('privacy', <Privacy />)} />
                            <Route path="/terms" element={visiblePage('terms', <Terms />)} />
                            <Route path="/secure-shopping" element={visiblePage('secure', <SecureShopping />)} />
                            <Route path="/copyright" element={visiblePage('copyright', <Copyright />)} />
                            <Route path="/payment" element={visiblePage('payment', <Payment />)} />
                            <Route path="/shipping" element={visiblePage('shipping', <Shipping />)} />
                            <Route path="/return" element={visiblePage('return', <Return />)} />
                            <Route path="/faq" element={visiblePage('faq', <FAQ />)} />
                            <Route path="/affiliate" element={visiblePage('affiliate', <Affiliate />)} />
                            <Route path="/sitemap" element={visiblePage('sitemap', <Sitemap />)} />
                            <Route path="*" element={<NotFound />} />
                          </Route>
                        </Routes>
                      </Suspense>
                    </PageVisibilityProvider>
                  </Router>
                </CurrencyProvider>
              </ConfirmProvider>
            </ToastProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
